import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { StatusBadge } from "@/components/Compliance";
import { NATION_META } from "@/lib/nations";
import { ShieldCheck, Printer, ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";

export default function EvidencePack() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [dl, setDl] = useState(false);
  useEffect(() => { api.get(`/properties/${id}/evidence`).then((r) => setD(r.data)).catch(() => {}); }, [id]);
  if (!d) return <div className="min-h-screen grid place-items-center text-muted-foreground">Preparing Evidence Pack…</div>;

  const downloadPdf = async () => {
    setDl(true);
    try {
      const res = await api.get(`/properties/${id}/evidence.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `certy-evidence-${d.property.name.replace(/\s+/g, "-")}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Branded PDF downloaded");
      const { data } = await api.post("/me/review-prompt");
      if (data.show) toast("Was that useful? A 30-second Trustpilot review helps other landlords find GetCerty.", { duration: 12000, action: { label: "Leave a review", onClick: () => window.open(data.url, "_blank") } });
    } catch { toast.error("Could not generate PDF"); }
    finally { setDl(false); }
  };

  return (
    <div className="min-h-screen bg-secondary/40">
      <div className="no-print sticky top-0 z-10 bg-navy text-white h-16 flex items-center justify-between px-4 sm:px-6">
        <Link to={`/properties/${id}`} className="inline-flex items-center gap-2 text-sm"><ArrowLeft size={16} /> Back to property</Link>
        <div className="flex items-center gap-2">
          <button data-testid="download-evidence-pdf" onClick={downloadPdf} disabled={dl} className="min-h-[40px] px-4 inline-flex items-center gap-2 rounded-xl bg-emerald-brand hover:bg-emerald-deep font-semibold transition-colors disabled:opacity-60"><Download size={16} /> {dl ? "Generating…" : "Download PDF"}</button>
          <button data-testid="print-evidence" onClick={() => window.print()} className="min-h-[40px] px-4 inline-flex items-center gap-2 rounded-xl bg-white/10 hover:bg-white/20 font-medium transition-colors"><Printer size={16} /> Print</button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto bg-white my-6 p-8 sm:p-12 shadow-lg rounded-lg" data-testid="evidence-pack">
        <div className="flex items-center justify-between border-b-2 border-navy pb-5">
          <div className="flex items-center gap-2 font-heading font-extrabold text-xl text-navy"><span className="grid place-items-center h-9 w-9 rounded-lg bg-emerald-brand text-white"><ShieldCheck size={20} /></span> GetCerty Evidence Pack</div>
          <div className="text-right text-xs text-slate-500">Generated {new Date(d.generated_at).toLocaleString()}</div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
          <div><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Property</div><div className="font-semibold text-navy">{d.property.name}</div><div>{d.property.address} {d.property.postcode}</div></div>
          <div><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Landlord</div><div className="font-semibold text-navy">{d.landlord.name}</div><div>{d.landlord.email}</div></div>
          <div><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Jurisdiction</div><div className="font-semibold text-navy">{NATION_META[d.property.nation]?.lawLabel}</div></div>
          <div><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Overall compliance</div><div className="font-heading text-2xl font-extrabold text-navy">{d.score}/100</div></div>
        </div>

        <h2 className="font-heading font-bold text-navy mt-8 mb-3">Compliance checklist</h2>
        <table className="w-full text-sm border-collapse">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b"><th className="py-2">Requirement</th><th>Issued</th><th>Expires</th><th>Status</th></tr></thead>
          <tbody>
            {d.checklist.map((it) => (
              <tr key={it.rule.id} className="border-b border-slate-100">
                <td className="py-2 pr-2 font-medium">{it.rule.name}</td>
                <td className="text-slate-600">{it.cert?.issue_date || "—"}</td>
                <td className="text-slate-600">{it.cert?.expiry_date || "—"}</td>
                <td><StatusBadge status={it.status} label={it.label} /></td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 className="font-heading font-bold text-navy mt-8 mb-3">Document register (with upload timestamps)</h2>
        {d.certificates.length === 0 ? <p className="text-sm text-slate-500">No documents uploaded.</p> : (
          <ul className="text-sm space-y-1">
            {d.certificates.map((c) => (
              <li key={c.id} className="flex justify-between border-b border-slate-100 py-1">
                <span>{c.cert_type} {c.status === "superseded" && <em className="text-slate-400">(superseded)</em>} {c.engineer_name ? `· ${c.engineer_name}` : ""}{c.served_date ? <span className="text-emerald-deep"> · served {c.served_date}</span> : ""}</span>
                <span className="text-slate-500">uploaded {new Date(c.uploaded_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}

        {d.tenancy && (<><h2 className="font-heading font-bold text-navy mt-8 mb-3">Current tenancy</h2>
          <p className="text-sm" data-testid="ev-tenancy"><span className="font-semibold">{d.tenancy.tenant_names}</span> · from {d.tenancy.start_date}{d.tenancy.end_date ? ` to ${d.tenancy.end_date}` : ""}{d.tenancy.deposit_amount ? ` · deposit £${d.tenancy.deposit_amount}${d.tenancy.deposit_scheme ? ` in ${d.tenancy.deposit_scheme}` : ""}${d.tenancy.deposit_protected_date ? ` (protected ${d.tenancy.deposit_protected_date})` : ""}` : ""}</p></>)}

        {d.readiness && (<><h2 className="font-heading font-bold text-navy mt-8 mb-3">{d.readiness.title}</h2>
          <ul className="text-sm space-y-1" data-testid="ev-readiness">{d.readiness.checks.map((c) => <li key={c.name} className="flex justify-between border-b border-slate-100 py-1"><span>{c.name}</span><span className={c.ok ? "text-emerald-deep font-medium" : "text-rose-600 font-medium"}>{c.ok ? "Met" : "Not met"}</span></li>)}</ul></>)}

        {d.documents?.length > 0 && (<><h2 className="font-heading font-bold text-navy mt-8 mb-3">Supporting documents</h2>
          <table className="w-full text-sm border-collapse" data-testid="ev-documents">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b"><th className="py-2">Document</th><th>Expires</th><th>Served</th><th>Status</th></tr></thead>
            <tbody>{d.documents.map((it) => (
              <tr key={it.entry.key} className="border-b border-slate-100">
                <td className="py-2 pr-2 font-medium">{it.entry.name}{!it.entry.required && <span className="text-slate-400 font-normal"> (optional)</span>}</td>
                <td className="text-slate-600">{it.doc?.expiry_date || "—"}</td>
                <td className="text-slate-600">{it.doc?.served_date || "—"}</td>
                <td>{it.status === "grey" ? <span className="text-xs text-slate-400">{it.label}</span> : <StatusBadge status={it.status} label={it.label} />}</td>
              </tr>))}</tbody>
          </table></>)}

        {d.notices.length > 0 && (<><h2 className="font-heading font-bold text-navy mt-8 mb-3">Notices & communications log</h2>
          <ul className="text-sm space-y-1">{d.notices.map((n) => <li key={n.id} className="border-b border-slate-100 py-1"><span className="capitalize font-medium">{n.type}:</span> {n.title} <span className="text-slate-500">— {new Date(n.date).toLocaleString()}</span></li>)}</ul></>)}

        <h2 className="font-heading font-bold text-navy mt-8 mb-3">Demonstrates compliance with</h2>
        <p className="text-xs text-slate-500 leading-relaxed">{d.nation_rules.join(" · ")}</p>
        <p className="mt-6 text-xs text-slate-400 border-t pt-4">This pack is generated from records held in GetCerty. Rule references should be verified against official sources. Documents are retained with an immutable audit trail.</p>
      </div>
    </div>
  );
}
